import { IResponse, ICountry, RECIPIENT_TYPE, IUser } from 'types';
import { openDoc, openQuery } from 'utils';
import { AxiosPrivateFirebaseInstance, AxiosWest3Instance } from 'settings';
import db from 'services/firestore';
import database from 'services/database';
import { IEntity, IEntitySettings, IMinimalEntity } from 'state/user';
import { IIntegration } from 'types/integrations';

export interface CreateUserPayload {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  companyCountry: ICountry['alpha2'];
  companyType: RECIPIENT_TYPE;
  companyName: string;
}

export const createUser = async (payload: CreateUserPayload) => {
  try {
    const { data } = await AxiosWest3Instance.post<IResponse>(
      'registration/user',
      payload
    );

    return data;
  } catch (error) {
    console.warn(error.message);
  }
};

export interface GetUserParams {
  id: string;
}

export const getUser = async ({
  id,
}: GetUserParams): Promise<IUser | undefined> => {
  try {
    const user = await db
      .collection('users')
      .doc(id)
      .get()
      // TODO: type openDoc properly so it returns correct type
      .then((doc) => openDoc(doc) as IUser);

    if (user) {
      return user;
    } else {
      console.warn('User not found.');
    }
  } catch (error) {
    console.warn(error.message);
  }

  return undefined;
};

export interface GetUserEntityParams {
  entityId: string;
}

export const getUserEntity = async ({ entityId }: GetUserEntityParams) => {
  try {
    const entity = await db
      .collection('entities')
      .doc(entityId)
      .get()
      .then((doc) => openDoc(doc));

    if (entity) {
      return entity;
    } else {
      console.warn('User not found.');
    }
  } catch (error) {
    console.warn(error.message);
  }

  return;
};

export interface ISubscribeToUserEntityIntegrationsSummaryParams
  extends GetUserEntityParams {
  callback: (integrationsSummary: IIntegration | null) => void;
}

export const subscribeToUserEntityIntegrationsSummary = ({
  entityId,
  callback,
}: ISubscribeToUserEntityIntegrationsSummaryParams) =>
  db
    .collection('entities')
    .doc(entityId)
    .collection('integrations')
    .doc('summary')
    .onSnapshot(
      (doc) => {
        callback(openDoc(doc));
      },
      (error) =>
        console.log(
          'Failed to subscribe to user entity integrations summary. Error: ',
          error
        )
    );

export interface ISubscribeToUserEntityParams extends GetUserEntityParams {
  callback: (entity: IEntity | null) => void;
}

export const subscribeToEntity = ({
  entityId,
  callback,
}: ISubscribeToUserEntityParams) => {
  return db
    .collection('entities')
    .doc(entityId)
    .onSnapshot((query) =>
      callback(openDoc<Omit<IEntity, 'integrations'>>(query))
    );
};

export interface RegisterUserConnectionParams {
  userId: string;
}

export const registerUserConnection = async ({
  userId,
}: RegisterUserConnectionParams) => {
  // Since I can connect from multiple devices or browser tabs, we store each connection instance separately
  // any time that connectionsRef's value is null (i.e. has no children) I am offline
  const myConnectionsRef = database.ref(`users/${userId}/connections`);
  const connectedRef = database.ref('.info/connected');

  connectedRef.on('value', (snap) => {
    if (snap.val() === true) {
      // We're connected (or reconnected)! Do anything here that should happen only if online (or on reconnect)
      const con = myConnectionsRef.push(true);

      // When I disconnect, remove this device
      con.onDisconnect().remove();
    }
  });
};

export interface RemoveUserConnectionsParams {
  userId: string;
}

export const removeUserConnections = async ({
  userId,
}: RemoveUserConnectionsParams) => {
  const snapshot = await database
    .ref(`users/${userId}/connections`)
    .once('value');

  if (snapshot.exists()) {
    await database.ref(`users/${userId}/connections`).remove();
  }
};

export const closeSuperAdminConnection = async () => {
  return AxiosPrivateFirebaseInstance.post<IResponse>(
    '/admin_actions/removeactonbehalfof'
  );
};

export interface GetEntityAccountDetailsParams {
  entityId: string;
}

export const getEntityAccountDetails = async ({
  entityId,
}: GetEntityAccountDetailsParams) => {
  return db
    .collection('entities')
    .doc(entityId)
    .collection('entityAccountDetails')
    .get()
    .then((query) => openQuery(query));
};

export const updateEntitySettings = async (payload: IEntitySettings) => {
  return AxiosPrivateFirebaseInstance.put<IResponse>(
    '/entities/settings',
    payload
  );
};

export const updateUser = async (payload: { user: Partial<IUser> }) => {
  return AxiosPrivateFirebaseInstance.post<IResponse>('/users', payload);
};

export const getUserEntities = async () => {
  const { data } = await AxiosPrivateFirebaseInstance.get<
    IResponse<{ entities: Array<IMinimalEntity> }>
  >(`users/entities`);

  if (data?.success) {
    return data.data;
  }

  throw new Error('Unable to retrieve user entities');
};

export const getUserEntitiesSummary = async ({
  entityId,
}: {
  entityId: string;
}) => {
  const { data } = await AxiosPrivateFirebaseInstance.get<IResponse>(
    `entities/${entityId}/summary`
  );

  if (data?.success) {
    return data.data;
  }

  throw new Error('Unable to retrieve client summary');
};
